/**
 * Session hand-off API — child specialist session → parent (twin) return value.
 *
 * The PARENT records a finished child's result by calling
 * POST /sessions/{parent}/summary {child_session_id}: the server PULLS a grounded
 * summary of the child and stores it as a PLAIN agent message
 * (role=agent, content_type=text) in the parent, so the twin reasons over the
 * child's outcome on its next turn. The child never pushes and is never archived
 * (it stays live + resumable).
 *
 * getSessionSummary is the underlying read primitive (pull any session's
 * summary); recordChildSummary is the parent-initiated fetch-and-store.
 */
import { api, ApiError } from '../client'
import { SessionSummaryResponseSchema } from '../schemas_session'
import { parseResponse } from '../validation'

export const handoffApi = {
  /** Pull a grounded summary of a session (read primitive). */
  getSessionSummary: (sessionId: string) =>
    api
      .get(`/sessions/${sessionId}/summary`)
      .then((res) =>
        parseResponse(
          SessionSummaryResponseSchema,
          res,
          `GET /sessions/${sessionId}/summary`,
        ),
      ),

  /**
   * Record a finished child's summary into its parent (the twin's return value).
   * The server pulls the child summary and stores it as a plain agent message.
   *
   * No Idempotency-Key: idempotency is enforced SERVER-SIDE by the deterministic
   * message id (childSummaryMessageID + ON CONFLICT DO NOTHING) — stronger than a
   * client-supplied key→result map and not dependent on the client sending one.
   * A retry / re-return converges on the same stored message.
   *
   * A 404 ("child has no content to summarise") is an EXPECTED, graceful outcome
   * — an empty/external-runtime child simply yields no inbound_summary card. It
   * is NOT a failure, so we resolve rather than throw: a rejected mutation
   * promise here went unhandled and detonated React's use()/cache dispatcher,
   * white-screening the session page on return-to-chat. Real failures (5xx,
   * network) still propagate.
   */
  recordChildSummary: async (parentSessionId: string, childSessionId: string): Promise<void> => {
    try {
      await api.post<void>(`/sessions/${parentSessionId}/summary`, { child_session_id: childSessionId })
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return
      throw err
    }
  },
}
